import { Bill } from '@enview/interface/types/bills/Bill';
import { faFacebook, faLinkedin, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faShareAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactElement, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { trackShare } from '../../../analytics/UserActionAnalytics';
import { g as abilityGlossary } from '../../../config/ability';
import { checkPermission } from '../../../dux';
import { State, Thunk } from '../../../dux/@types';
import CustomToggleSelectDropDown, {
  CustomToggleDropdownOption,
} from '../DropDowns/CustomToggleSelectDropDown';
import ConfirmActionPopover from '../Popovers/ConfirmActionPopover';
import GateTooltip, { GateTooltipFeature } from '../Tooltips/TooltipGate';
import EmailBillForm from './EmailBillForm';

type BillShareButtonProps = {
  bill: Bill;
};

const BillShareButton = (props: BillShareButtonProps): ReactElement => {
  const { bill } = props;
  const [emailBillPopoverVisible, setEmailBillPopoverVisible] = useState(false);
  const [gatedTooltipVisible, setGatedTooltipVisible] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const orgUser = useSelector((state: State) => state.account.organizationUser);

  const currentPageUrl = encodeURIComponent(window.location.href);

  const canShareBill = (): Thunk<boolean> =>
    dispatch(checkPermission(abilityGlossary.SEND, abilityGlossary.BILL));

  const getDropdownOptions = (): CustomToggleDropdownOption[] => {
    return canShareBill()
      ? [
          {
            icon: <FontAwesomeIcon color="#979797" icon={faEnvelope} />,
            label: t('bill.header.share.emailText'),
            value: 1,
            onSelect: () => setEmailBillPopoverVisible(true),
          },
          {
            icon: <FontAwesomeIcon color="#979797" icon={faLinkedin} />,
            label: t('bill.header.share.linkedInText'),
            value: 2,
            onSelect: () => {
              trackShare(
                {
                  id: bill.id,
                  shareType: 'linkedIn',
                  dataType: 'bill',
                  sharedUrl: currentPageUrl,
                },
                orgUser,
              );
              window.open(
                t('bill.header.share.linkedInUrl', {
                  currentPageUrl,
                }),
              );
            },
          },
          {
            icon: <FontAwesomeIcon color="#979797" icon={faXTwitter} />,
            label: t('bill.header.share.twitterText'),
            value: 3,
            onSelect: () => {
              trackShare(
                {
                  id: bill.id,
                  shareType: 'twitter',
                  dataType: 'bill',
                  sharedUrl: currentPageUrl,
                },
                orgUser,
              );
              window.open(
                t('bill.header.share.twitterUrl', {
                  currentPageUrl,
                }),
              );
            },
          },
          {
            icon: <FontAwesomeIcon color="#979797" icon={faFacebook} />,
            label: t('bill.header.share.facebookText'),
            value: 4,
            onSelect: () => {
              trackShare(
                {
                  id: bill.id,
                  shareType: 'facebook',
                  dataType: 'bill',
                  sharedUrl: currentPageUrl,
                },
                orgUser,
              );
              window.open(
                t('bill.header.share.facebookUrl', {
                  currentPageUrl,
                }),
              );
            },
          },
        ]
      : [];
  };

  const shareButtonToggle = (
    <button
      className={`btn btn-sm btn-secondary toggle tracking-toggle ${
        !canShareBill() ? 'disabled' : ''
      }`}
      type="button"
    >
      <span className="icon">
        <FontAwesomeIcon icon={faShareAlt} />
      </span>
      {t('bill.header.share.buttonText')}
    </button>
  );

  return (
    <div className="expand-dropdown-options" id="bill-header-share-button">
      <CustomToggleSelectDropDown
        onClickEvent={() => {
          if (!canShareBill()) setGatedTooltipVisible(true);
        }}
        options={getDropdownOptions()}
        toggle={shareButtonToggle}
      />
      {gatedTooltipVisible && (
        <GateTooltip
          accountType="free"
          anchorId={'bill-header-share-button'}
          customContent={
            <Trans
              components={{
                shareLink: <a href={t('urls.createAccount')} target="_blank" />,
              }}
              i18nKey="featureGating.tooltipText.shareButton"
              key="featureGating.tooltipText.shareButton"
            />
          }
          featureName={GateTooltipFeature.BillSharing}
          place="bottom"
        />
      )}
      <ConfirmActionPopover
        className="email-bill-popover"
        closePopup={() => setEmailBillPopoverVisible(false)}
        content={
          <EmailBillForm
            bill={bill}
            onDone={() => {
              setTimeout(() => setEmailBillPopoverVisible(false), 3000);
              trackShare(
                {
                  id: bill.id,
                  shareType: 'email',
                  dataType: 'bill',
                  sharedUrl: currentPageUrl,
                },
                orgUser,
              );
            }}
          />
        }
        show={emailBillPopoverVisible}
        target={<div />}
        toggleCallback={() => setEmailBillPopoverVisible(!emailBillPopoverVisible)}
      />
    </div>
  );
};

export default BillShareButton;
